import React, { useEffect, useState } from "react";
import AppModal from "../../components/layout/app/AppModal";
import { useToggleTop } from "../../components/common/ToggleTop";
import { apiMessage } from "../../api/apiMessage";
import useAlive from "../../hooks/useAlive";
import { UserService } from "../../services/UserService";
import { User } from "../../storage/store";
import { CAMERA_BRANDS, succeeded } from "./cameraUtils";

const FAILED = "Couldn't add the camera. Check the serial and verification code and try again.";

// The app's AddCamDevice (app/screens/cam/AddCamDevice.js), after choosing the
// camera's brand: a camera added by its serial number and verification code
// (on the camera's label), posted to hikvision/{propertyId}/device as
// { deviceSerial, verificationCode }. The brands are CAMERA_BRANDS; with only
// one, it's chosen already.
function AddCamDevice({ open, onClose, onAdded }) {
  const toast = useToggleTop();
  const alive = useAlive();
  const [brand, setBrand] = useState(null);
  const [deviceSerial, setDeviceSerial] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBrand(CAMERA_BRANDS.length === 1 ? CAMERA_BRANDS[0] : null);
    setDeviceSerial("");
    setVerificationCode("");
    setSaving(false);
  }, [open]);

  const ready = deviceSerial.trim() && verificationCode.trim();

  const save = async (event) => {
    event.preventDefault();
    if (!ready || saving) return;
    setSaving(true);
    let response;
    try {
      response = await UserService.addHIKDevice(User.getPropertyId(), {
        deviceSerial: deviceSerial.trim(),
        verificationCode: verificationCode.trim(),
      });
    } catch (error) {
      if (!alive.current) return;
      setSaving(false);
      toast(apiMessage(error?.msg ? { msg: error.msg } : null, FAILED), "red");
      return;
    }
    if (!alive.current) return;
    if (!succeeded(response)) {
      setSaving(false);
      toast(apiMessage({ msg: response?.msg }, FAILED), "red");
      return;
    }
    toast(response?.msg || "Camera added", "green");
    onAdded();
  };

  if (open && !brand) {
    return (
      <AppModal
        open
        onClose={onClose}
        title="Choose a brand"
        actions={
          <button type="button" className="hx-btn is-ghost" onClick={onClose}>
            Cancel
          </button>
        }
      >
        <p className="hx-modal-text">Pick your camera's brand.</p>
        <ul className="hx-cam-brands">
          {CAMERA_BRANDS.map((item) => (
            <li key={item.key}>
              <button type="button" className="hx-cam-brand" onClick={() => setBrand(item)}>
                <span className="hx-cam-brand-name">{item.name}</span>
                <span className="hx-cam-brand-hint">{item.hint}</span>
              </button>
            </li>
          ))}
        </ul>
      </AppModal>
    );
  }

  return (
    <AppModal
      open={open}
      onClose={() => !saving && onClose()}
      title={brand ? `Add ${brand.name} camera` : "Add camera"}
      actions={
        <>
          <button type="button" className="hx-btn is-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form="hx-cam-add-form" className="hx-btn" disabled={!ready || saving}>
            {saving ? "Adding…" : "Add"}
          </button>
        </>
      }
    >
      {CAMERA_BRANDS.length > 1 ? (
        <button type="button" className="hx-cam-brand-change" onClick={() => setBrand(null)} disabled={saving}>
          Not {brand?.name}? Choose another brand
        </button>
      ) : null}
      <form id="hx-cam-add-form" className="hx-name-form" onSubmit={save} noValidate>
        <label className="hx-label" htmlFor="hx-cam-add-serial">
          Enter Your Device Serial
        </label>
        <input
          id="hx-cam-add-serial"
          className="hx-input"
          value={deviceSerial}
          maxLength={40}
          autoComplete="off"
          spellCheck={false}
          disabled={saving}
          onChange={(event) => setDeviceSerial(event.target.value)}
        />
        <label className="hx-label" htmlFor="hx-cam-add-code">
          Enter Verification Code
        </label>
        <input
          id="hx-cam-add-code"
          className="hx-input"
          type="password"
          value={verificationCode}
          maxLength={40}
          autoComplete="off"
          disabled={saving}
          onChange={(event) => setVerificationCode(event.target.value)}
        />
      </form>
    </AppModal>
  );
}

export default AddCamDevice;
